import { isWeb } from './platform';

// eslint-disable-next-line @typescript-eslint/no-var-requires
let Keychain: ReturnType<typeof require> | undefined;

if (!isWeb) {
  // react-native-keychain - not available for web and not necessary;
  Keychain = require('react-native-keychain');
}

export const EXPIRATION_TIME_FOR_FORCE_RETRIEVE_IN_MS = 5 * 60 * 1000;

// MMKV: Synchronous persistent storage (native + web compatible)
// Native: Uses MMKV (30x faster than AsyncStorage)
// Web: Uses localStorage (synchronous browser storage)
let storage: ReturnType<typeof require> | null = null;
let storageInitialized = false;

const getStorage = () => {
  if (isWeb) {
    return null; // Web uses localStorage directly
  }

  if (!storageInitialized) {
    try {
      const { MMKV } = require('react-native-mmkv');
      storage = new MMKV();
      storageInitialized = true;
    } catch (error) {
      console.error('[Storage] MMKV initialization failed:', error.message);
      throw error;
    }
  }

  return storage;
};

// Keys that live in sessionStorage on web (per-tab auth isolation).
// Everything else stays in localStorage (shared preferences).
const WEB_SESSION_KEYS = new Set([
  'token',
  'refreshToken',
  'expiresIn',
  'tokenUpdating',
  'env',
  'email',
  'role',
  'name',
  'firstName',
  'lastName',
  'tid',
  'deviceCode',
  'accounts',
  'status',
  'featureEnv',
  'sessionStartTime',
  'lastURL',
  'appHubID',
  'signalRAnonymID',
  '_sf_active_account_id', // what reads homeAccountId from MSAL lib
]);

// On web, routes auth keys to sessionStorage and everything else to localStorage.
const getWebStorage = (key: string): Storage => {
  return WEB_SESSION_KEYS.has(key) ? sessionStorage : localStorage;
};

export const getSecureValue = (key: string) => {
  if (isWeb) {
    return getWebStorage(key).getItem(key) || false;
  }

  // Native: use MMKV (lazy initialization)
  const mmkv = getStorage();
  return mmkv.getString(key) || false;
};

export const setSecureValue = (key: string, value: string | null) => {
  if (isWeb) {
    const store = getWebStorage(key);
    if (!value) {
      store.removeItem(key);
    } else {
      store.setItem(key, value);
    }
  } else {
    // Native: MMKV is synchronous (lazy initialization)
    const mmkv = getStorage();
    if (!value) {
      mmkv.delete(key);
    } else {
      mmkv.set(key, value);
    }
  }
};

export const checkiCloudPassword = async () => {
  if (isWeb) {
    return '';
  }
  return await Keychain.getInternetCredentials('login.smarfac.com', { synchronized: true });
};

export const savePasswordToiCloud = async (server: string, email: string, pass: string) => {
  if (isWeb) {
    return '';
  }
  await Keychain.setInternetCredentials(server, email, pass, { synchronized: true });
};

export const removeSecureValue = (key: string) => {
  setSecureValue(key, null);
};

export const setExpirationTimeAsTimestamp = async (key: string, timeInterval: number = EXPIRATION_TIME_FOR_FORCE_RETRIEVE_IN_MS) => {
  const now = Date.now();
  const expirationTime = now + timeInterval;

  setSecureValue(key, expirationTime.toString());

  console.log('Expiration timestamp:', expirationTime);
  return expirationTime;
};

export const isExpiredTimestamp = async (key: string) => {
  const storedTimestamp = getSecureValue(key);

  if (!storedTimestamp) {
    return true;
  }

  const expirationTime = parseInt(storedTimestamp);
  const now = Date.now();

  return now > expirationTime;
};
